import { Injectable } from '@angular/core';
import { ConfirmationRequest, ContentBlock } from '../../../data/service/agent-chat.service';

/** A tool the AI used in a turn, as the chat shows it. */
export interface AiChatToolStep {
  label: string;
  /** null while running. */
  ok: boolean | null;
  /** What it looked up, e.g. "Period: last month". */
  detail?: string;
  /** How long it took. */
  ms?: number;
  /** When it started (while running). */
  startedAt?: number;
}

export interface AiChatMessage {
  role: 'user' | 'ai' | 'confirm' | 'notice';
  text: string;
  blocks?: ContentBlock[];
  timestamp: Date;
  /** Tools the AI used for this answer. */
  tools?: AiChatToolStep[];
  /** How long the turn took, and whether its steps are shown. */
  runMs?: number;
  runOpen?: boolean;
  /** Admin-only: where the turn's time went, e.g. "Routing 0.4s · Setup 0.2s · …". */
  timings?: string;
  /** role 'notice': an admin-only note (why the chat had no Synced tools). */
  admin?: boolean;
  /** An answer that failed; `retryText` is what to send again. */
  error?: { code?: string; retryText?: string };
  /** role 'confirm': the write waiting for the user, and what they chose. */
  confirmation?: ConfirmationRequest;
  confirmationState?: 'pending' | 'confirmed' | 'cancelled';
  decidedAt?: Date;
}

/**
 * SD-1244: the AI chat's conversation, kept outside the panel so the header's and the dashboard's
 * panels show the same one and it survives moving between pages. One organisation per
 * conversation: it is cleared when the organisation changes.
 */
@Injectable({ providedIn: 'root' })
export class AiChatStateService {
  messages: AiChatMessage[] = [];
  sessionId: string | null = null;
  resolvedAgentId: string | null = null;
  lastUserMessage = '';
  organisationName = '';
  organisationId: string | null = null;
  inputHistory: string[] = [];

  /** Starts a new conversation (the organisation is kept). */
  reset(): void {
    this.messages = [];
    this.sessionId = null;
    this.resolvedAgentId = null;
    this.lastUserMessage = '';
  }

  /** Ties the conversation to an organisation; a different one starts afresh. Returns whether it changed. */
  useOrganisation(organisationId: string | null, organisationName: string): boolean {
    const changed = !!this.organisationId && !!organisationId && this.organisationId !== organisationId;
    if (changed) {
      this.reset();
    }
    if (organisationId) {
      this.organisationId = organisationId;
    }
    if (organisationName) {
      this.organisationName = organisationName;
    }
    return changed;
  }

  /** The confirmation still waiting for an answer, if any. */
  get pendingConfirmation(): AiChatMessage | null {
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const m = this.messages[i];
      if (m.role === 'confirm' && m.confirmationState === 'pending') {
        return m;
      }
    }
    return null;
  }
}
